// PLAN.md §3.5.1 — the only click target inside cells.
// Four states driven by the local player's current cell.

import {
  CELL_SPECS,
  STAKE_PER_ENTRY,
  type CellNonSpectator,
} from '@trading-grounds/shared'
import { Button, DeltaNumber, StatCell } from '../ported/index.js'
import { AnimateNumber } from 'motion-plus/react'
import { useStore } from '../../store.js'
import { sendMove } from '../../socket.js'
import { fmtUsd } from '../../format.js'

const SPEC_BY_CELL = new Map(CELL_SPECS.map((s) => [s.cell, s]))

export function CellAction({ cell }: { cell: CellNonSpectator }) {
  const self = useStore((s) => s.self)
  const spec = SPEC_BY_CELL.get(cell)
  if (!spec || !self) return null

  const localCell = self.optimistic_target ?? self.active_cell
  const isLocalInThisCell = localCell === cell
  const isLiquidated = isLocalInThisCell && self.active_status === 'liquidated'

  // State 3/4 — local player IS in this cell
  if (isLocalInThisCell && !isLiquidated) {
    return <PositionSummary cell={cell} />
  }

  // State 1/2 — Button
  const directionLabel =
    spec.direction === 'short' ? `${spec.display_lev}x short` : `${spec.display_lev}x long`
  const arrow = spec.direction === 'short' ? '↓' : '↑'

  let label: string
  let variant: 'primary' | 'secondary'

  if (self.active_cell === 'spectator' || isLiquidated) {
    label = `Enter ${directionLabel}`
    variant = 'primary'
  } else {
    label = `Switch ${directionLabel} ${arrow}`
    variant = 'secondary'
  }

  return (
    <Button
      variant={variant}
      size="md"
      loading={self.optimistic_busy && self.optimistic_target === cell}
      onClick={() => sendMove(cell)}
      className="w-full"
    >
      {label}
    </Button>
  )
}

function PositionSummary({ cell }: { cell: CellNonSpectator }) {
  const self = useStore((s) => s.self)!
  const entryPrice = self.active_position_entry_price ?? 0
  const stake = self.active_stake
  const pnl = stake - STAKE_PER_ENTRY
  const pnlPct = (pnl / STAKE_PER_ENTRY) * 100
  void cell

  return (
    <div className="flex flex-col gap-1.5 px-3 py-2 rounded-lg bg-black/5 w-full">
      <Row
        label="entry"
        value={<span className="text-label text-black tabular-nums">{fmtUsd(entryPrice)}</span>}
      />
      <Row
        label="stake"
        value={
          <span className="text-label text-black tabular-nums">
            <AnimateNumber
              prefix="$"
              locales="en-US"
              format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
            >
              {stake}
            </AnimateNumber>
          </span>
        }
      />
      <Row
        label="PnL"
        value={
          <DeltaNumber
            value={pnl}
            format="usd"
            compoundPercent={pnlPct}
            size="sm"
            precision={2}
          />
        }
      />
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-label text-grey">{label}</span>
      {value}
    </div>
  )
}

// Re-export StatCell signal for tests/usage
void StatCell
